import formatMoney from '../../core/formatMoney';
import { isApplePayCompatible } from '../../core/helpers.js';
import { getSizedImageURL } from '../../core/images';
import { closeMobileNav } from '../header/mobile-nav';
import productTileTemplate from '../collection/productTileTemplateNosto';
import shippingProgress from './shipping-progress';

export default class AddToBagDrawer {
  elements;
  drawerType;
  addedItem;
  liveRegionSanitizerObserver;

  constructor() {
    this.drawerType = 'add-to-bag';
    this.addedItem = null;

    this.elements = {
      drawerElement: document.querySelector('[data-slider-add-to-bag]'),
      overlay: document.querySelector('[data-full-overlay]'),
      closeButton: document.querySelector('.slider-add-to-bag__close'),
      quickViewDesktop: document.getElementById('quicklook-modal'),
      cartCountElements: document.querySelectorAll('[data-cart-count]'),
      cartSubtotal: document.querySelector('[data-slider-added-to-bag-totals] [data-subtotal]'),
      cartProductsContainer: document.querySelector('[data-slider-added-to-bag-products]'),
      liveRegion: document.querySelector('[data-slider-add-to-bag-live-region]'),
      additionalButtonsContainer: document.querySelector('[data-slider-add-to-bag-show-additional]'),
      nostoRecommendations: document.querySelector('[data-slider-add-to-bag-recommendations-nosto]'),
      applePayButton: null,
      googlePayButton: null,
    };

    if (!this.elements.drawerElement) {
      return;
    }

    // Keep live announcements outside the drawer because it can be aria-hidden.
    this.sanitizeDrawerLiveRegions();
    this.observeDrawerLiveRegions();

    document.addEventListener('astr-nosto-minicart-recommendations-loaded', (e) => {
      console.log('astr-nosto-minicart-recommendations-loaded event received', e.detail);
      this.updateNostoRecommendationsComponent(e?.detail?.data?.products || []);
    });
  };

  sanitizeDrawerLiveRegions = () => {
    if (!this.elements.drawerElement) {
      return;
    }

    this.elements.drawerElement
      .querySelectorAll('[data-slider-added-to-bag-products], [data-slider-add-to-bag-recommendations-nosto], .nosto_element[aria-live]')
      .forEach((el) => {
        el.removeAttribute('aria-live');
      });
  };

  observeDrawerLiveRegions = () => {
    if (this.liveRegionSanitizerObserver || typeof MutationObserver === 'undefined' || !this.elements.drawerElement) {
      return;
    }

    this.liveRegionSanitizerObserver = new MutationObserver(() => {
      this.sanitizeDrawerLiveRegions();
    });

    this.liveRegionSanitizerObserver.observe(this.elements.drawerElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-live'],
    });
  };

  /**
   * Update the cart count in the UI
   * @returns {void}
   */
  updateCartCount = () => {
    if (!this.elements.cartCountElements.length) return;

    const cartCount = window.theme.cart.data?.item_count || 0;

    this.elements.cartCountElements.forEach((el) => {
      el.textContent = cartCount;
    });
  };

  /**
   * Update the cart totals in the UI
   * @returns {void}
   */
  updateCartTotals = () => {
    if (!this.elements.cartSubtotal) return;

    const cartTotal = window.theme.cart.data?.total_price || 0;

    this.elements.cartSubtotal.innerHTML = formatMoney(cartTotal, theme.moneyFormat);
  };

  updateCartProducts = () => {
    if (!this.elements.cartProductsContainer || !this.addedItem) return;

    this.elements.cartProductsContainer.innerHTML = '';
    const item = this.addedItem;
    item.image_resized = window.theme.collections.placeholder;
    item.discount_title = '';

    if (typeof item.image !== 'undefined' && item.image !== null && item.image !== '') {
      item.image_resized = getSizedImageURL(item.image, '140x');
    }

    item.price_formatted = formatMoney(item.price, theme.moneyFormat);
    item.original_price_formatted = formatMoney(item.original_price, theme.moneyFormat);
    item.final_price_formatted = formatMoney(item.final_price, theme.moneyFormat);
    // item.line_price_formatted = formatMoney(item.line_price, theme.moneyFormat);
    // item.discounted_price_formatted = formatMoney(item.discounted_price, theme.moneyFormat);
    // item.original_line_price_formatted = formatMoney(item.original_line_price, theme.moneyFormat);
    // item.final_line_price_formatted = formatMoney(item.final_line_price, theme.moneyFormat);

    if (item.discounts && item.discounts.length > 0) {
      // Only 1 supported at this time
      const discount = item.discounts[0];
      item.original_price_formatted = item.original_price_formatted.replace('money', 'money original');
      // item.discounted_price_formatted = item.discounted_price_formatted.replace('money', 'money discounted');
      // item.original_line_price_formatted = item.original_line_price_formatted.replace('money', 'money original');
      item.final_price_formatted = item.final_price_formatted.replace('money', 'money discounted');

      if (typeof discount.title !== 'undefined') {
        item.discount_title = `<span class="discount-title">${discount.title}</span>`;
      }
    }

    const productElement = document.createElement('div');
    productElement.classList.add('slider-add-to-bag__item', 'slider-add-to-bag-item');
    productElement.setAttribute('tabindex', '0');
    productElement.setAttribute('aria-label', `${item.title}, ${formatMoney(item.price, theme.moneyFormat)}`);
    productElement.setAttribute('data-product-handle', item.handle);
    productElement.setAttribute('data-product-id', item.id);
    productElement.setAttribute('data-variant-id', item.variant_id);

    productElement.innerHTML = `
      <div class="slider-add-to-bag-item__image">
        <a href="${window.Shopify.routes.root}products/${item.handle}" title="${item.product_title}" aria-label="${item.product_title}">
          <img loading="lazy" src="${item.image_resized}" width="80" alt="${item.featured_image?.alt || item.product_title}" />
        </a>
      </div>
      <div class="slider-add-to-bag-item__options">
        <a class="slider-add-to-bag-item__title" href="${window.Shopify.routes.root}products/${item.handle}">${item.product_title}</a>
        ${item.discounts && item.discounts.length > 0
        ? `<p class="slider-add-to-bag-item__price has-discount">${item.discounted_price === 0
          ? `<span class="money free">FREE</span>${item.original_price_formatted}${item.discount_title}`
          : `${item.final_price_formatted}<span class="price__discount">${item.original_price_formatted}</span>${item.discount_title}`
        }</p>`
        : `<p class="slider-add-to-bag-item__price">${item.original_price_formatted}</p>`
      }
        ${item.properties && item.properties['Note']
        ? `<p class="slider-add-to-bag-item__message">${item.properties['Note']}</p>`
        : ''
      }
        ${item.variant_options && item.variant_options[0] && item.variant_options[1]
        ? `<p class="slider-add-to-bag-item__color"><span>COLOR: </span>${item.variant_options[0].replace('Plus Size - ', '')}</p>
              <p class="slider-add-to-bag-item__size"><span>SIZE: </span>${item.variant_options[1]}</p>`
        : ''
      }
        <a class="slider-add-to-bag-item__view-bag hidden--desktop" href="${window.Shopify.routes.root}cart">View shopping bag (<span data-cart-count></span>)</a>
      </div>
    `;

    this.elements.cartProductsContainer.appendChild(productElement);

    if (this.elements.liveRegion) {
      this.elements.liveRegion.textContent = `${item.product_title} added to bag`;
    }

    this.elements.cartCountElements = document.querySelectorAll('[data-cart-count]');
  };

  requestNostoRecommendations = () => {
    if (!this.elements.nostoRecommendations) return;

    const recommendationsInstance = window.theme?.api?.nosto?.recommendations?.instance;
    if (!recommendationsInstance) return;

    const placement_name = this.elements.nostoRecommendations.getAttribute('id');
    if (!placement_name) return;

    // Request the new recommendation data from Nosto, we'll receive a custom event when loaded
    recommendationsInstance.loadDynamicRecommendation(placement_name);
    // window.theme.api.nosto.recommendations.instance.recommendationsData
  };

  updateNostoRecommendationsComponent = (products) => {
    if (products.length === 0 || !this.elements.nostoRecommendations) return;

    this.elements.nostoRecommendations.innerHTML = '';

    const products_tiles_html = products.map((product) => productTileTemplate(product, {
      is_slider: false,
      is_simplified: true,
      has_swatches: false,
      has_quickview: false,
      is_search: false,
      is_lookbook: false,
      enable_price_conversion: false,
      has_animations: false
    })).join('');

    if (products_tiles_html === '') {
      this.elements.nostoRecommendations.remove();
      return;
    }

    this.elements.nostoRecommendations.innerHTML = products_tiles_html;
    this.sanitizeDrawerLiveRegions();
  };

  updateAdditionalCheckoutButtons = () => {
    if (!this.elements.additionalButtonsContainer) return;

    let attemptsApplePay = 0;
    let attemptsGooglePay = 0;
    const interval = window.setInterval(() => {
      if (document.querySelector('shopify-apple-pay-button')) {
        window.clearInterval(interval);
        this.elements.applePayButton = document.querySelector('shopify-apple-pay-button');
        this.elements.additionalButtonsContainer.appendChild(this.elements.applePayButton);
      }

      attemptsApplePay++;
      if (attemptsApplePay >= 5) {
        window.clearInterval(interval);
        console.log('Apple Pay button not found');

        const intervalGooglePay = window.setInterval(() => {
          attemptsGooglePay++;
          // Default to Google Pay if Apple Pay not found
          if (document.querySelector('shopify-google-pay-button')) {
            window.clearInterval(intervalGooglePay);
            this.elements.googlePayButton = document.querySelector('shopify-google-pay-button');
            this.elements.additionalButtonsContainer.appendChild(this.elements.googlePayButton);
          }

          if (attemptsGooglePay >= 5) {
            window.clearInterval(intervalGooglePay);
            console.log('Google Pay button not found');
          }
        }, 500);
      }
    }, isApplePayCompatible ? 500 : 1);
  };

  // Function name needs to be the same as in CartDrawer class
  updateDrawer = async (error_message = null, open_cart = false, init = false) => {
    this.updateCartProducts();
    this.updateCartTotals();
    this.updateCartCount();
    this.requestNostoRecommendations();
    this.updateAdditionalCheckoutButtons();
    shippingProgress();

    // Use this.addedItem
    this.openSliderCart();

    this.addedItem = null;
  };

  setAddedItem = (item) => {
    this.addedItem = item;
  };

  // Function name needs to be the same as in CartDrawer class
  getDrawerType = () => {
    return this.drawerType;
  };

  // Function name needs to be the same as in CartDrawer class
  openSliderCart = () => {
    closeMobileNav();

    document.documentElement.classList.add('slider-cart-opened');

    if (this.elements.drawerElement) {
      this.elements.drawerElement.removeAttribute('inert');
      this.elements.drawerElement.setAttribute('aria-hidden', 'false');
    }

    if (this.elements.overlay) {
      this.elements.overlay.classList.add('active', 'black');
    }
    if (document.querySelector('[data-parent="account"]')) {
      document.querySelector('[data-parent="account"]').classList.remove('keep-opened');
    }
    if (document.querySelector('[data-parent="search"]')) {
      document.querySelector('[data-parent="search"]').classList.remove('keep-opened');
    }
    if (document.querySelector('[data-handle="account"] a')) {
      document.querySelector('[data-handle="account"] a').setAttribute('aria-expanded', false);
    }
    if (document.querySelector('[data-handle="search"] a')) {
      document.querySelector('[data-handle="search"] a').setAttribute('aria-expanded', false);
    }

    // [ADA] Move focus to opened cart
    this.elements.drawerElement.focus();

    // [ADA] Set focus on the first element in the cart
    this.elements.closeButton?.focus();

    // The scrollbar isn't there anymore, some calculated sections such as the carousels need to have their width reajusted
    window.dispatchEvent(new Event('resize'));
  };

  // Function name needs to be the same as in CartDrawer class
  closeSliderCart = () => {
    document.documentElement.classList.remove('slider-cart-opened');
    this.elements.overlay.classList.remove('active', 'black');

    if (this.elements.drawerElement) {
      this.elements.drawerElement.setAttribute('aria-hidden', 'true');
      this.elements.drawerElement.setAttribute('inert', '');
    }

    // [ADA] Close button has been called
    this.elements.closeButton?.setAttribute('aria-pressed', true);

    // The scrollbar comes back, some calculated sections such as the carousels need to have their width reajusted
    window.dispatchEvent(new Event('resize'));
  };
}
